import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * JoyForm cart upsell.
 *
 * Submits the add-on forms from snippets/joy-cart-upsell.liquid through the same
 * cart/add.js + CartLinesUpdateEvent flow as product-form.js, so the cart drawer,
 * cart page and cart bubble all re-render in place. Listens on document because
 * the forms are replaced every time the cart section re-renders.
 */
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-joy-upsell')) return;

  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  const error = form.querySelector('[data-joy-upsell-error]');
  if (button?.disabled) return;
  if (button) button.disabled = true;
  if (error) error.hidden = true;

  const formData = new FormData(form);
  const sectionIds = new Set();
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.add(element.dataset.sectionId);
  }
  if (sectionIds.size) {
    formData.append('sections', [...sectionIds].join(','));
    formData.append('sections_url', window.location.pathname);
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(formData.get('id')), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    }).then((res) => res.json());

    if (response.status) {
      // Nothing changed in the cart, so reject instead of resolving: a resolve without
      // sections makes the cart re-render and would wipe the error message.
      deferred.reject(new Error(response.description || response.message || 'Add to cart failed'));
      showError(error, response.description || response.message);
      return;
    }

    const cart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((res) => res.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: response.sections,
        source: 'joy-cart-upsell',
        didError: false,
      },
    });
  } catch (err) {
    deferred.reject(err);
    showError(error, 'Could not add this item. Please try again.');
  } finally {
    if (button) button.disabled = false;
  }
});

/**
 * @param {Element | null} element
 * @param {string} message
 */
function showError(element, message) {
  if (!(element instanceof HTMLElement)) return;
  element.textContent = message;
  element.hidden = false;
}
